import React, { useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import LockIcon from '@mui/icons-material/Lock';
import CrownIcon from '@src/assets/images/crown.png';
import Typography from '@src/new-components/Typography';
import CommonButton from '@src/new-components/Button';
import { COLOR } from '@src/styles/color';
import useUserInfo from '@src/hooks/useTtsUser';
import useWindowDimensions from '@src/hooks/useWindowDimensions';
import ROUTES from '@src/constants/route';
import { BREAKPOINTS } from '@src/styles/config';
import {
  checkFreePackage,
  checkCustomPackage,
  checkVipPackage,
} from '@src/services/package';
import ActionDialog from '@src/new-components/Dialog/ActionDialog';
import actions from '@src/redux/actions';
import { StyledVoiceCloningOption } from './index.style';

const InstantVoiceCloningOption = ({
  handleOpenPopupInstantVoiceCloning,
  numOfInstantVoiceCloning = 0,
}) => {
  const { width } = useWindowDimensions();
  const { t } = useTranslation();
  const { ttsUser } = useUserInfo();
  const history = useHistory();
  const dispatch = useDispatch();

  const { usingPackage } = useSelector((state) => state.user);
  const { user } = useSelector((state) => state.auth);
  const { maxInstantVoiceCloning } = user.studioUsageOptions || {};

  const [showPopupWithUserVipOrCustom, setShowPopupWithUserVipOrCustom] =
    useState(false);
  const [showPopupWithUserOtherPackage, setShowPopupWithUserOtherPackage] =
    useState(false);

  const { packageCode } = ttsUser;
  const isFreePackage = checkFreePackage(packageCode);
  const isUserPackageNotExpired =
    ttsUser?.packageExpiryDate && moment().isBefore(ttsUser?.packageExpiryDate);
  const userCanUseInstantVoiceCloning =
    isFreePackage || isUserPackageNotExpired;

  const userHasNotReachedMaxInstantVoiceCloning =
    numOfInstantVoiceCloning < maxInstantVoiceCloning;

  // Check if user is using VIP or custom package
  const isPackageVipV3 = checkVipPackage({
    type: usingPackage?.type,
    level: usingPackage.level,
  });
  const isCustomPackage = checkCustomPackage({
    type: usingPackage?.type,
    level: usingPackage.level,
  });

  const handleStartInstantVoiceCloning = () => {
    if (userHasNotReachedMaxInstantVoiceCloning) {
      handleOpenPopupInstantVoiceCloning();
      return;
    }
    if (isPackageVipV3 || isCustomPackage) {
      setShowPopupWithUserVipOrCustom(true);
      return;
    }

    setShowPopupWithUserOtherPackage(true);
  };

  const handleActivatePackage = () => {
    history.push(ROUTES.PAYMENT);
    dispatch(actions.voiceVoiceCloning.closePopupVoiceCloningOptions());
  };

  const handleConfirm = () => {
    setShowPopupWithUserVipOrCustom(false);
  };

  return (
    <StyledVoiceCloningOption>
      <div className="header">
        <div className="icon-container">
          <FlashOnIcon className="icon" />
        </div>
        <div className="badge-wrapper">
          <Typography variant="body-14-medium" color={COLOR.indigo.default}>
            {t('instantVoiceCloningProperty')}
          </Typography>
        </div>
      </div>
      <div className="description-wrapper">
        <div className="label-wrapper">
          <Typography variant="body-24-bold" color={COLOR.black.default}>
            {t('instantVoiceCloningOption')}
          </Typography>
        </div>
        <Typography variant="body-14-regular" color={COLOR.label2[64]}>
          {t('instantVoiceCloningDescription')}
        </Typography>
      </div>

      {userCanUseInstantVoiceCloning ? (
        <div className="action-wrapper">
          <CommonButton
            variant="contained"
            color="primary"
            size="large"
            onClick={handleStartInstantVoiceCloning}
            textProps={{
              variantText: 'body-14-medium',
              color: COLOR.secondary[100],
            }}
            customStyle={{
              padding: '10px 24px',
            }}
          >
            {t('startVoiceCloning')}
          </CommonButton>
        </div>
      ) : (
        <div className="upgrade-package-action-wrapper">
          <div className="upgrade-package-desc">
            <div className="icon-wrapper">
              <LockIcon className="icon" />
            </div>
            <Typography variant="body-14-regular" color={COLOR.secondary[100]}>
              {t('requireActivatePackageToUseVoiceCloning')}
            </Typography>
          </div>
          <div className="upgrade-package-action">
            <CommonButton
              fullWidth={width < BREAKPOINTS.MLG}
              variant="outlineSmall"
              onClick={handleActivatePackage}
              textProps={{
                variantText: 'body-14-medium',
                color: COLOR.secondary[100],
              }}
              customStyle={{
                padding: '9px 12px',
              }}
            >
              {t('activatePackage')}
            </CommonButton>
          </div>
        </div>
      )}

      {showPopupWithUserVipOrCustom && (
        <ActionDialog
          open={showPopupWithUserVipOrCustom}
          title={t('userHasReachedMaxInstantVoiceCloning')}
          onClose={() => setShowPopupWithUserVipOrCustom(false)}
          description={t('notiWithUserUsingVipOrCustomPackage', {
            numOfVoiceCloning: maxInstantVoiceCloning,
          })}
          image={CrownIcon}
          imageContainerSize="100px"
          fullWidth
          actionComponent={
            <CommonButton
              variant="contained"
              onClick={handleConfirm}
              textProps={{
                variantText: 'body-14-bold',
                color: COLOR.indigo[100],
              }}
            >
              {t('gotIt')}
            </CommonButton>
          }
        />
      )}

      {showPopupWithUserOtherPackage && (
        <ActionDialog
          open={showPopupWithUserOtherPackage}
          title={t('userHasReachedMaxInstantVoiceCloning')}
          description={t('notiWithUserUsingOtherPackage', {
            numOfVoiceCloning: maxInstantVoiceCloning,
          })}
          image={CrownIcon}
          imageContainerSize="100px"
          onClose={() => setShowPopupWithUserOtherPackage(false)}
          fullWidth
          actionComponent={
            <CommonButton
              variant="contained"
              onClick={handleActivatePackage}
              textProps={{
                variantText: 'body-14-bold',
                color: COLOR.indigo[100],
              }}
            >
              {t('upgradePackageAction')}
            </CommonButton>
          }
        />
      )}
    </StyledVoiceCloningOption>
  );
};

export default InstantVoiceCloningOption;
